import { cloneDeep } from 'lodash';
import { defineTransform } from '../transform';
import { merge, updateOrRemove } from '../utils';
import { transformOpRange } from '../transformCursor';
import { Editor } from 'slate';

export function applyProperties(annotation, newProperties) {
  const changes = Object.entries(newProperties);

  for (const [key, value] of changes) {
    annotation[key] = value;
  }
  return annotation;
}

function SetAnnotationTransform(editor, op) {
  const { key } = op.properties;
  const annotation = editor.metadata.annotations[key];
  let wasRef = false;

  if (annotation.range?.unref) {
    annotation.range = annotation.range.unref();
    wasRef = true;
  }

  applyProperties(annotation, op.newProperties);
  const range =
    annotation.range && Object.hasOwn(annotation.range, 'current')
      ? annotation.range.current
      : annotation.range;

  if (range && wasRef) {
    annotation.range = Editor.rangeRef(editor, range, {
      affinity: 'inward',
    });
  }

  // If range is null, make sure we set to the correct shape
  if (!annotation.range) {
    annotation.range = {
      current: null,
      affinity: 'inward',
    };
  }
}

export default SetAnnotationTransform;

function transformSetAnnotationOp(setAnnotationOp, op) {
  let tOp = setAnnotationOp;
  tOp = transformOpRange(tOp, 'properties', op);
  tOp = transformOpRange(tOp, 'newProperties', op);
  return tOp;
}

defineTransform('set_annotation', 'insert_text', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'remove_text', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'split_node', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'merge_node', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'move_node', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'insert_node', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'remove_node', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'set_node', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'add_mark', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'remove_mark', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'set_mark', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'set_selection', (op1, op2, side) => {
  return transformSetAnnotationOp(op1, op2);
});

defineTransform('set_annotation', 'add_annotation', (op1, op2, side) => {
  if (op1.properties.key !== op2.annotation.key) return op1;

  // Since the add has already happened, our old properties need to
  // match the current state of the annotation.
  const tOp = {
    ...cloneDeep(op1),
    properties: op2.annotation,
  };
  return updateOrRemove(tOp, ['data', 'properties'], op2.data.annotation);
});

defineTransform('set_annotation', 'remove_annotation', (op1, op2, side) => {
  if (op1.properties.key !== op2.annotation.key) return op1;

  // set after remove can have no effect
  return [];
});

defineTransform('set_annotation', 'set_annotation', (op1, op2, side) => {
  if (op1.properties.key !== op2.properties.key) return op1;

  if (side !== 'left') return [];

  // Since the set has already happened, our old properties need to
  // match the current state of the annotation.
  const mergedProperties = merge(op2.properties, op2.newProperties);
  const mergedData = merge(
    op2.data?.properties || {},
    op2.data?.newProperties || {}
  );

  let tOp = {
    ...op1,
    properties: mergedProperties,
  };
  tOp = updateOrRemove(tOp, ['data', 'properties'], mergedData);

  return tOp;
});

defineTransform('set_annotation', 'add_metadata', (op1, op2, side) => op1);
defineTransform('set_annotation', 'remove_metadata', (op1, op2, side) => op1);
defineTransform('set_annotation', 'set_metadata', (op1, op2, side) => op1);
